import { ModuloLicitacao, TipoAlvo, TipoAtividade, moduloDoUuid, type AlvoDto, type AtividadeDto, type ModuloLayoutDto } from '@nexital/shared';

// Regras puras da área da licitação (RQ-LIC-001 a RQ-LIC-004).

/** Posição padrão de cada módulo, como na referência B (coordenadas do quadro da licitação). */
const PADRAO: Record<ModuloLicitacao, Omit<ModuloLayoutDto, 'modulo' | 'versao'>> = {
  [ModuloLicitacao.Sobre]: { x: 0, y: 0, largura: 520, altura: 370 },
  [ModuloLicitacao.Pessoas]: { x: 0, y: 400, largura: 520, altura: 280 },
  [ModuloLicitacao.Documentos]: { x: 550, y: 0, largura: 600, altura: 400 },
  [ModuloLicitacao.Notas]: { x: 550, y: 430, largura: 600, altura: 250 },
  [ModuloLicitacao.ProximosPassos]: { x: 1180, y: 0, largura: 340, altura: 680 },
  [ModuloLicitacao.Cronograma]: { x: 0, y: 710, largura: 1520, altura: 150 },
  [ModuloLicitacao.Historico]: { x: 0, y: 880, largura: 1520, altura: 120 },
};

export const MODULOS = Object.keys(PADRAO).map(Number) as ModuloLicitacao[];

/** Os sete módulos: a posição salva (compartilhada) ou a padrão, com versão 0. */
export function layoutDosModulos(salvo: ModuloLayoutDto[]): ModuloLayoutDto[] {
  const porModulo = new Map(salvo.map((m) => [m.modulo, m]));
  return MODULOS.map((modulo) => porModulo.get(modulo) ?? { modulo, ...PADRAO[modulo], versao: 0 });
}

/** Anel "N de M" dos Próximos passos: só atividades e entregas, não marcos. */
export function progressoPassos(atividades: AtividadeDto[]) {
  const passos = atividades.filter((a) => a.tipo !== TipoAtividade.Marco);
  return { concluidos: passos.filter((a) => a.concluidaEm).length, total: passos.length };
}

export type MarcoNaLinha = AtividadeDto & { posicao: number };

/**
 * Marcos com prazo na linha do Cronograma, com a posição (0 a 1) entre o primeiro e o último;
 * `hoje` na mesma escala (fora de 0–1 quando antes ou depois de todos), ou null sem marcos.
 */
export function linhaDoTempo(atividades: AtividadeDto[], agora: Date): { marcos: MarcoNaLinha[]; hoje: number | null } {
  const comPrazo = atividades
    .filter((a) => a.tipo === TipoAtividade.Marco && a.prazo)
    .map((a) => ({ ...a, instante: new Date(a.prazo!).getTime() }))
    .sort((a, b) => a.instante - b.instante);
  if (!comPrazo.length) return { marcos: [], hoje: null };
  const inicio = comPrazo[0]!.instante;
  const duracao = comPrazo.at(-1)!.instante - inicio || 1;
  const posicao = (instante: number) => (instante - inicio) / duracao;
  return {
    marcos: comPrazo.map(({ instante, ...a }) => ({ ...a, posicao: posicao(instante) })),
    hoje: posicao(agora.getTime()),
  };
}

export type TipoArquivo = 'pdf' | 'word' | 'planilha' | 'imagem' | 'zip' | 'outro';

const TIPOS: Record<string, TipoArquivo> = {
  pdf: 'pdf',
  doc: 'word',
  docx: 'word',
  rtf: 'word',
  odt: 'word',
  xls: 'planilha',
  xlsx: 'planilha',
  ods: 'planilha',
  csv: 'planilha',
  png: 'imagem',
  jpg: 'imagem',
  jpeg: 'imagem',
  tif: 'imagem',
  tiff: 'imagem',
  bmp: 'imagem',
  zip: 'zip',
};

export const tipoDoArquivo = (extensao: string): TipoArquivo => TIPOS[extensao.toLowerCase()] ?? 'outro';

/** Módulo onde o alvo de um post-it ou seta aparece na licitação; null quando não é módulo nem item. */
export function moduloDoAlvo(alvo: AlvoDto, atividades: AtividadeDto[]): ModuloLicitacao | null {
  if (alvo.tipo === TipoAlvo.Modulo) return moduloDoUuid(alvo.uuid);
  if (alvo.tipo === TipoAlvo.Documento) return ModuloLicitacao.Documentos;
  if (alvo.tipo === TipoAlvo.Pessoa) return ModuloLicitacao.Pessoas;
  if (alvo.tipo !== TipoAlvo.Atividade) return null;
  const marco = atividades.find((a) => a.uuid === alvo.uuid)?.tipo === TipoAtividade.Marco;
  return marco ? ModuloLicitacao.Cronograma : ModuloLicitacao.ProximosPassos;
}
